'use client';

import type { ReactNode } from 'react';
import { m, useReducedMotion, type Variants } from 'motion/react';
import { usePathname } from 'next/navigation';

import { fadeSlideUp, staggerContainer } from '@/utils/animation/variants';

type TAnimatedTag = 'div' | 'section' | 'ol' | 'ul' | 'li';

type AnimatedListProps = {
  children: ReactNode;
  className?: string;
  /** The element to render — `ol`/`li` keep list semantics under the stagger. */
  as?: TAnimatedTag;
  /** Motion variants for the container; defaults to the site stagger. */
  variants?: Variants;
};

export const AnimatedList = ({ children, className, as = 'div', variants = staggerContainer }: AnimatedListProps) => {
  const prefersReducedMotion = useReducedMotion();
  const pathname = usePathname();
  const Component = m[as];

  return (
    <Component
      key={pathname}
      initial={prefersReducedMotion ? false : 'hidden'}
      animate="visible"
      variants={variants}
      className={className}
    >
      {children}
    </Component>
  );
};

export const AnimatedListItem = ({
  children,
  className,
  as = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: TAnimatedTag;
}) => {
  const Component = m[as];

  return (
    <Component variants={fadeSlideUp} className={className}>
      {children}
    </Component>
  );
};
